// shared-components/hooks/useAnimationHooks.js
import { useAnimation } from "../../core/context/AnimationContext";
import { useInView } from "react-intersection-observer";

/**
 * Hook to create fade-in animations for components
 * @param {Object} options - Animation options
 * @param {number} options.delay - Delay in seconds
 * @param {number} options.distance - Distance to move in pixels
 * @param {string} options.direction - Direction to fade from ('up', 'down', 'left', 'right')
 * @returns {Object} Animation variants and props
 */
export function useFadeIn(options = {}) {
  const { enabled, reducedMotion, duration, easing, componentAnimations } =
    useAnimation();

  const { delay = 0, distance = 50, direction = "up" } = options;

  // Determine initial position based on direction
  let initialX = 0;
  let initialY = 0;

  switch (direction) {
    case "up":
      initialY = distance;
      break;
    case "down":
      initialY = -distance;
      break;
    case "left":
      initialX = distance;
      break;
    case "right":
      initialX = -distance;
      break;
    default:
      initialY = distance;
  }

  // Create variants for animate presence
  const variants = {
    hidden: {
      opacity: 0,
      y: reducedMotion ? 0 : initialY,
      x: reducedMotion ? 0 : initialX,
    },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      transition: {
        duration: reducedMotion ? 0 : duration,
        ease: easing,
        delay,
      },
    },
  };

  // Always call hooks unconditionally
  const [inViewRef, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // If animations are disabled, return empty variants
  if (!enabled || !componentAnimations) {
    return {
      variants: {
        hidden: {},
        visible: {},
      },
      shouldAnimate: false,
      initial: "visible",
      animate: "visible",
      inViewRef: null,
      inView: true,
    };
  }

  return {
    variants,
    shouldAnimate: true,
    initial: "hidden",
    animate: inView ? "visible" : "hidden",
    inViewRef,
    inView,
  };
}

/**
 * Hook to create hover animations for interactive elements
 * @param {Object} options - Animation options
 * @returns {Object} Animation props for hover states
 */
export function useHoverAnimation(options = {}) {
  const { enabled, reducedMotion, duration, componentAnimations } =
    useAnimation();

  const { scale = 1.05, shadow = true, rotate = 0, color = false } = options;

  // If animations disabled or reduced motion, return empty props
  if (!enabled || !componentAnimations || reducedMotion) {
    return { whileHover: {}, whileTap: {} };
  }

  // Create hover animation
  const hoverAnimation = {
    scale: scale,
    boxShadow: shadow ? "0px 5px 15px rgba(0, 0, 0, 0.1)" : undefined,
    rotate: rotate,
    transition: { duration: duration / 2 },
  };

  // Create tap animation (slightly smaller scale)
  const tapAnimation = {
    scale: scale - 0.05,
    boxShadow: shadow ? "0px 2px 5px rgba(0, 0, 0, 0.1)" : undefined,
    rotate: rotate / 2,
    transition: { duration: duration / 3 },
  };

  // If color animation, add color transition
  if (color) {
    hoverAnimation.backgroundColor = color;
  }

  return {
    whileHover: hoverAnimation,
    whileTap: tapAnimation,
  };
}

/**
 * Hook for list item staggered animations
 * @param {Object} options - Animation options
 * @param {number} options.staggerChildren - Delay between children in seconds
 * @returns {Object} Animation variants for parent and children
 */
export function useStaggerAnimation(options = {}) {
  const { enabled, reducedMotion, duration, componentAnimations } =
    useAnimation();

  const { staggerChildren = 0.1, delayChildren = 0.1, childVariants } = options;

  // If animations disabled, return empty variants
  if (!enabled || !componentAnimations || reducedMotion) {
    return {
      parentVariants: {},
      childVariants: {},
      shouldAnimate: false,
    };
  }

  // Default child variants if not provided
  const defaultChildVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration },
    },
  };

  // Parent container variants to stagger children
  const parentVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren,
        delayChildren,
      },
    },
  };

  return {
    parentVariants,
    childVariants: childVariants || defaultChildVariants,
    shouldAnimate: true,
  };
}
